import type { DefaultDtoType, Field } from "../Field";
import { initField } from "../Field";
import type { InferOutput } from "valibot";
import { object, string } from "valibot";
import { dataName, label } from "../../utils/validation/base";
import { useTranslation } from "../../state/i18n";
import { TextInput } from "../../components/inputs/TextInput";
import { useFormServices } from "../../services/formServices";
import { TEXT_CONDITION_KEYS } from "../../constants";
import { defaultNumberFilter } from "../../pages/customer/form/filter";
import { FieldButtonPreview, PreviewContentLabel } from "../FieldPreviewComponents";
import PropertySection from "../PropertySection";
import { keyFromSelector } from "i18next";

const Schema = object({
  label_text: label(),
  data_name: dataName(),
});
const ValueSchema = string();

export type StopwatchFieldProperties = InferOutput<typeof Schema>;
type ValueType = InferOutput<typeof ValueSchema>;

const StopwatchField: Field<StopwatchFieldProperties, DefaultDtoType, ValueType> = {
  type: "stopwatch",
  category: "dateTime",
  widget: "com.moreapps.plugins:stopwatch:1",
  schema: Schema,
  valueSchema: ValueSchema,
  hasData: true,
  defaultProperties: () => ({}),
  icon: "stopwatch",
  titleKey: keyFromSelector(($) => $.STOPWATCH_FIELD_LABEL),
  descriptionKey: keyFromSelector(($) => $.STOPWATCH_FIELD_DESCRIPTION),
  propertyKeys: {
    label_text: keyFromSelector(($) => $.FIELD_PROPERTY_LABEL_TEXT),
    data_name: keyFromSelector(($) => $.FIELD_PROPERTY_DATA_NAME),
  },
  ruleBehaviour: {
    conditions: { keys: TEXT_CONDITION_KEYS, variant: "text" },
    actions: { variant: "text" },
  },
  filter: {
    type: "number",
    definition: defaultNumberFilter,
  },
  stringValue: (value) => value,
  renderValue: (value) => value,
  PreviewContent: (props) => {
    const { t } = useTranslation();

    return (
      <PreviewContentLabel uid={props.uid} label={props.properties.label_text}>
        <FieldButtonPreview icon="clock" text={t(($) => $.STOPWATCH_FIELD_PREVIEW_START_BUTTON_LABEL)} />
      </PreviewContentLabel>
    );
  },
  EditItem: (props) => {
    const { t } = useTranslation();
    const { fieldService } = useFormServices();

    return (
      <>
        <PropertySection>
          <TextInput
            label={t(props.propertyKeys.label_text)}
            value={props.data.properties.label_text}
            onBlur={(e) => fieldService.setLabelProperty(props.uid, e.target.value)}
            errorMessage={props.getError("label_text")}
            required
          />
        </PropertySection>

        <PropertySection title={t(($) => $.FIELD_PROPERTIES_SECTION_ADVANCED)}>
          <TextInput
            label={t(props.propertyKeys.data_name)}
            value={props.data.properties.data_name}
            onBlur={(e) => props.setValue("data_name", e.target.value)}
            required
            errorMessage={props.getError("data_name")}
            info={{
              title: t(props.propertyKeys.data_name),
              alt: t(($) => $.FIELD_PROPERTY_DESCRIPTION_ARIA_LABEL, { property: props.propertyKeys.data_name }),
              description: t(($) => $.FIELD_PROPERTY_DATA_NAME_DESCRIPTION),
            }}
          />
        </PropertySection>
      </>
    );
  },
};

export default initField(StopwatchField);
